import React, { useCallback, useMemo, useRef } from 'react';
import cn from 'bem-cn';

import { PromptStatus } from '@ai-platform/utils';
import prompthubEllipse from '../../../../images/icons/prompthub-ellipse.svg';

import { RoutePaths } from '../../../../constants';
import { Button, Link, Stack, Video } from '../../../UI';
import { useLike, MetrikaGoal, useMetrika } from '../../../../hooks';
import { openAliceChat } from '../../../../utils/alice/alice';

import { usePromptCardVisibility } from '../../../../VisibilityTracker';
import { Like } from '../../components/Like';
import { PromptCardProps } from '../../PromptCard.types';

import './PromptCard.mobile.scss';

const b = cn('prompt-card-new-mobile');

export const PromptCardNewMobile = (props: PromptCardProps) => {
    const { prompt, needMarkAsViewed } = props;
    const {
        title,
        description,
        videoData: { url: videoUrl },
        imageData: { urlOptimized: imageUrl },
        categories,
    } = prompt;

    const panelRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const authorImage = prompt.author?.image || prompthubEllipse;
    const isEditorial = prompt.author?.isEditor;
    const isPhotoRevival = categories?.includes('photo-revival');
    const isCharacters = categories?.includes('characters');
    const isPublished = prompt.status === PromptStatus.Published;

    const { likes, isLiked, toggleLike } = useLike({
        promptId: Number(prompt.id),
        initialLikes: prompt.likes || 0,
        initialIsLiked: prompt.isLiked || false,
    });

    const { reachGoal } = useMetrika();

    const handleOpenAliceChat = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            openAliceChat(prompt?.id);
            reachGoal(MetrikaGoal.ClickoutChat);
        },
        [prompt, reachGoal],
    );

    const promptLink = useMemo(() => {
        return [isPublished ? RoutePaths.Prompt : RoutePaths.NewPromptStatus, prompt.id].join('/');
    }, [prompt.id, isPublished]);

    usePromptCardVisibility(panelRef, { prompt, needMarkAsViewed, videoRef });

    const renderProfile = () => {
        if (authorImage) {
            return <img src={authorImage} alt={'Аватарка'} className={b('header', 'avatar')} />;
        }

        return <div />;
    };

    const renderMedia = () => {
        if (isPhotoRevival && Boolean(videoUrl)) {
            return (
                <Video
                    className={b('main', 'media', 'video')}
                    ref={videoRef}
                    src={videoUrl}
                    poster={imageUrl}
                    muted
                    loop
                    playsInline
                    preload="auto"
                />
            );
        }

        return (
            <img
                src={imageUrl}
                alt={title}
                className={b('main', 'media', 'image', { 'editorial-character': isCharacters && isEditorial })}
            />
        );
    };

    return (
        <Link to={promptLink} state={{ fromClient: true }} ref={panelRef}>
            <Stack className={b()}>
                <Stack className={b('header')} direction="row">
                    {renderProfile()}
                    <Like
                        className={b('header', 'like')}
                        count={likes}
                        isEditorial
                        isLiked={isLiked}
                        onClick={toggleLike}
                    />
                </Stack>
                <Stack className={b('main')}>
                    <Stack className={b('main', 'media', { 'editorial-character': isCharacters && isEditorial })}>
                        {renderMedia()}
                    </Stack>
                    <p className={b('main', 'text')}>{description || title}</p>
                </Stack>
                <Stack className={b('footer')}>
                    <Button className={b('footer', 'button')} variant="primary" fullWidth onClick={handleOpenAliceChat}>
                        Попробовать в Алисе AI
                    </Button>
                </Stack>
            </Stack>
        </Link>
    );
};
